import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileDownload,
  faRunning,
  faChalkboardTeacher,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { db } from "@lib/firebase";
import {
  collection,
  query,
  where,
  getDoc,
  doc,
  getDocs,
  limit,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@cakrawalajakarta/shared-components/common/NotificationContext";
import {
  SectionQuicklinks,
  SectionRundown,
} from "@cakrawalajakarta/shared-components";
import axios from "axios";
import Iframe from "react-iframe";

const SkeletonLoader = () => (
  <div className="w-full min-h-screen py-20 text-center text-cyan-600 flex-col justify-center relative bg-slate-100">
    <div className="relative z-10 container mx-auto px-4">
      <div className="bg-white rounded-2xl border border-slate-200 text-left relative overflow-hidden max-w-4xl mx-auto">
        <div className="w-full h-80 bg-gray-200 animate-pulse"></div>
        <div className="p-4 space-y-4">
          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    </div>
  </div>
);

// Utility untuk debounce
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Utility untuk format tanggal ke "D MMMM YYYY"
const formatDate = (dateString) => {
  if (!dateString) return "Date not determined";
  try {
    let date;
    if (
      typeof dateString === "object" &&
      "toDate" in dateString &&
      typeof dateString.toDate === "function"
    ) {
      date = dateString.toDate();
    } else if (typeof dateString === "object" && "seconds" in dateString) {
      date = new Date(dateString.seconds * 1000);
    } else {
      date = new Date(dateString);
    }
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    return "Date not determined";
  } catch {
    return "Date not determined";
  }
};

export const KegiatanDetail = React.memo(() => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [viewMode, setViewMode] = useState("map");
  const [collectionName, setCollectionName] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [snapToken, setSnapToken] = useState(null);
  const [isSnapReady, setIsSnapReady] = useState(false);

  const fetchCollectionName = useCallback(async (eventId) => {
    try {
      const eventRef = doc(db, "events", eventId);
      const eventDoc = await getDoc(eventRef);
      return eventDoc.exists() ? eventDoc.data().collectionName : null;
    } catch {
      return null;
    }
  }, []);

  const fetchEventData = useCallback(async () => {
    try {
      const eventDoc = await getDoc(doc(db, "semuaKegiatan", slug));
      if (!eventDoc.exists()) {
        setError("Event not found");
        setLoading(false);
        return null;
      }

      const eventData = { id: eventDoc.id, ...eventDoc.data() };
      setEvent(eventData);
      setLoading(false);
      return eventData;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return null;
    }
  }, [slug]);

  const checkRegistration = useCallback(
    async (collectionName, eventId) => {
      if (!user || !eventId || !collectionName)
        return { isRegistered: false, status: null, token: null };

      try {
        const q = query(
          collection(db, collectionName),
          where("uid", "==", user.uid),
          where("eventId", "==", eventId),
          limit(1)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          const data = doc.data();
          const status = data.transaction_status || "unknown";
          const paymentType = data.payment_type || "unknown";
          const harga = data.harga || 0;

          if (
            (status === "settlement" &&
              (paymentType === "free" || harga === 0)) ||
            (status === "pending" && paymentType !== "free") ||
            (status === "settlement" &&
              paymentType !== "free" &&
              data.snapToken)
          ) {
            setRegistrationStatus("registered");
            setTransactionStatus(status);
            setSnapToken(data.snapToken || null);

            if (status === "pending" && data.snapToken && data.orderId) {
              const response = await axios.get(
                `${import.meta.env.VITE_API_BASE_URL}/api/payment/status/${
                  data.orderId
                }`,
                { params: { eventId } }
              );
              const updatedStatus = response.data.transaction_status;
              if (updatedStatus !== status) {
                if (
                  updatedStatus === "pending" ||
                  updatedStatus === "settlement"
                ) {
                  await updateDoc(doc.ref, {
                    transaction_status: updatedStatus,
                  });
                  setTransactionStatus(updatedStatus);
                  return {
                    isRegistered: true,
                    status: updatedStatus,
                    token: data.snapToken,
                  };
                } else {
                  await deleteDoc(doc.ref);
                  setRegistrationStatus("not_registered");
                  setTransactionStatus(null);
                  setSnapToken(null);
                  return { isRegistered: false, status: null, token: null };
                }
              }
            }
            return { isRegistered: true, status, token: data.snapToken };
          } else {
            await deleteDoc(doc.ref);
            setRegistrationStatus("not_registered");
            setTransactionStatus(null);
            setSnapToken(null);
            return { isRegistered: false, status: null, token: null };
          }
        } else {
          setRegistrationStatus("not_registered");
          return { isRegistered: false, status: null, token: null };
        }
      } catch {
        setError("Failed to check registration status. Please try again.");
        return { isRegistered: false, status: null, token: null };
      }
    },
    [user]
  );

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      const eventData = await fetchEventData();
      if (!eventData || !mounted) return;

      if (eventData.eventId) {
        try {
          const [fetchedCollectionName, registrationData] = await Promise.all([
            fetchCollectionName(eventData.eventId),
            user
              ? checkRegistration(null, eventData.eventId)
              : Promise.resolve({
                  isRegistered: false,
                  status: null,
                  token: null,
                }),
          ]);

          if (mounted) {
            setCollectionName(fetchedCollectionName);
            if (fetchedCollectionName && user) {
              const { isRegistered, status, token } = await checkRegistration(
                fetchedCollectionName,
                eventData.eventId
              );
              setRegistrationStatus(
                isRegistered ? "registered" : "not_registered"
              );
              setTransactionStatus(status);
              setSnapToken(token);
            }
          }
        } catch {
          if (mounted) setCollectionName(null);
        }
      }
    };

    fetchData();
    return () => {
      mounted = false;
    };
  }, [fetchEventData, fetchCollectionName, checkRegistration, user]);

  useEffect(() => {
    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);
    return () => clearInterval(checkSnap);
  }, []);

  const handleBayar = useCallback(async () => {
    if (!isSnapReady || !window.snap) {
      showNotification("error", "Payment system is not ready. Please try again.");
      return;
    }

    if (!snapToken) {
      showNotification("error", "Payment token not found.");
      return;
    }

    try {
      window.snap.pay(snapToken, {
        onSuccess: async () => {
          showNotification("success", "Payment successful!");
          const { status } = await checkRegistration(
            collectionName,
            event.eventId
          );
          setTransactionStatus(status);
          navigate("/myregistration");
        },
        onPending: async () => {
          showNotification("warning", "Payment is being processed.");
          const { status } = await checkRegistration(
            collectionName,
            event.eventId
          );
          setTransactionStatus(status);
          navigate("/myregistration");
        },
        onError: () => {
          showNotification("error", "Payment failed. Please try again.");
        },
        onClose: () => {
          // No logging
        },
      });
    } catch {
      showNotification("error", "Failed to process payment.");
    }
  }, [
    isSnapReady,
    snapToken,
    collectionName,
    event,
    showNotification,
    navigate,
    checkRegistration,
  ]);

  const handleRegister = useCallback(
    (category) => {
      if (
        !event ||
        !event.title ||
        (category.price == null && event.price == null) ||
        !event.eventId
      ) {
        showNotification(
          "error",
          "Event data is incomplete. Please try again."
        );
        return;
      }

      if (!user) {
        showNotification(
          "error",
          "Please login first to register."
        );
        navigate("/login");
        return;
      }

      if (!collectionName) {
        showNotification(
          "error",
          "Failed to load event data. Please try again."
        );
        return;
      }

      const title =
        event.type === "seminar"
          ? event.title
          : `${event.title} - ${category.categoryName || ""}`;
      const path =
        event.type === "seminar"
          ? `/formseminar/${event.slug}`
          : `/form/${event.slug}`;

      if (registrationStatus === "registered") {
        if (transactionStatus === "settlement") {
          showNotification("info", "You are already registered for this event.");
        } else if (transactionStatus === "pending") {
          handleBayar();
        }
        return;
      }

      const price = category.price ?? event.price ?? 0;

      navigate(path, { state: { title, price, eventId: event.eventId } });
    },
    [
      event,
      user,
      collectionName,
      registrationStatus,
      transactionStatus,
      showNotification,
      navigate,
      handleBayar,
    ]
  );

  const handleExportGpx = useCallback(
    debounce(async (gpxFileUrl, selectedCategory, event) => {
      try {
        const response = await fetch(gpxFileUrl, { mode: "cors" });
        const gpxText = await response.text();
        const blob = new Blob([gpxText], { type: "application/gpx+xml" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const fileName =
          event.type === "seminar"
            ? `${event.title.toLowerCase().replace(/\s+/g, "-")}.gpx`
            : `${event.title.toLowerCase().replace(/\s+/g, "-")}-${
                selectedCategory.distance
              }km.gpx`;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch {
        showNotification("error", "Failed to download GPX file.");
      }
    }, 500),
    []
  );

  useEffect(() => {
    if (event?.type === "seminar" && event.categories?.length > 0) {
      setSelectedCategoryIndex(0); // Pastikan hanya kategori pertama untuk seminar
    }
  }, [event]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = useMemo(() => {
    if (!event?.categories) return [];
    // Filter out Vertical (7K) and sort categories in the specified order
    const categoryOrder = [
      "Fun Trail (7K)",
      "Classic Distance (20K)",
      "Short Distance (40K)",
      "Long Distance (80K)",
      "Mix Group (20K)",
    ];
    return event.categories
      .filter((category) => category.categoryName !== "Vertical (7K)")
      .sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a.categoryName);
        const bIndex = categoryOrder.indexOf(b.categoryName);
        return aIndex - bIndex;
      });
  }, [event]);

  const selectedCategory = useMemo(
    () => categories[selectedCategoryIndex] || {},
    [categories, selectedCategoryIndex]
  );
  const isRegistrationOpen = useMemo(
    () => event?.status === "Registrasi Dibuka" || event?.status === "Registration Open",
    [event]
  );

  const getEventIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "trail_running":
        return { icon: faRunning, size: "text-5xl" };
      case "seminar":
        return { icon: faChalkboardTeacher, size: "text-4xl" };
      default:
        return { icon: faCalendarAlt, size: "text-4xl" };
    }
  };

  if (loading) {
    return <SkeletonLoader />;
  }

  if (error || !event) {
    return (
      <section className="w-full min-h-screen py-20 text-center text-cyan-600 flex-col justify-center relative bg-slate-100">
        <motion.h2
          className="text-xl sm:text-2xl md:text-3xl font-normal uppercase mb-4 text-cyan-600"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          EVENT
        </motion.h2>
        <motion.p
          className="text-4xl sm:text-5xl md:text-7xl font-black mx-auto mb-16 text-cyan-600 px-4"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {error || "NOT FOUND"}
        </motion.p>
        <Link to="/kegiatan" className="text-cyan-600 underline">
          Back to Event List
        </Link>
      </section>
    );
  }

  return (
    <section className="w-full min-h-screen py-20 text-center text-cyan-600 flex-col justify-center relative bg-slate-100">
      <div className="relative z-10 container mx-auto px-4">
        <motion.div
          className="bg-white rounded-2xl border border-slate-200 text-left relative overflow-hidden max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          <div className="relative w-full h-80">
            <img
              src={event.image || "https://via.placeholder.com/800x400"}
              alt={event.title}
              className="w-full h-80 object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-slate-600 bg-opacity-50 flex flex-col items-center justify-center gap-6">
              <FontAwesomeIcon
                icon={getEventIcon(event.type).icon}
                className={`text-white ${getEventIcon(event.type).size} z-10`}
                aria-label={`Ikon untuk ${
                  event.type === "trail_running" ? "Trail Running" : "Seminar"
                }`}
              />
              <div className="z-10 text-center">
                <h3 className="text-2xl md:text-4xl font-black uppercase text-white px-4">
                  {event.title}
                </h3>
                {event.type === "trail_running" &&
                  selectedCategory &&
                  selectedCategory.categoryName && (
                    <p className="text-lg md:text-xl uppercase text-white mt-2">
                      {selectedCategory.categoryName}
                    </p>
                  )}
              </div>
            </div>
          </div>

          {categories.length > 1 && (
            <div className="md:hidden bg-gray-100 text-base font-normal text-gray-400">
              <div className="flex flex-col divide-y divide-gray-300">
                {categories.map((category, index) => (
                  <button
                    key={index}
                    className={`w-full text-center uppercase px-4 py-4 block transition-colors duration-300 ${
                      selectedCategoryIndex === index
                        ? "bg-cyan-600 font-normal text-white"
                        : "hover:bg-gray-200"
                    }`}
                    onClick={() => setSelectedCategoryIndex(index)}
                  >
                    {category.categoryName}
                  </button>
                ))}
                <div className="border-b border-gray-300" />
              </div>
            </div>
          )}

          {categories.length > 1 && (
            <div className="hidden md:flex bg-gray-200 text-base font-normal text-gray-400 border-b border-gray-300">
              <div className="flex w-full divide-x divide-gray-300">
                {categories.map((category, index) => (
                  <button
                    key={index}
                    className={`flex-1 text-center uppercase px-4 py-4 transition-colors duration-300 ${
                      selectedCategoryIndex === index
                        ? "bg-gray-100 font-normal text-cyan-600"
                        : "hover:bg-gray-300"
                    }`}
                    onClick={() => setSelectedCategoryIndex(index)}
                  >
                    {category.categoryName}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(event.type === "trail_running" || event.type === "seminar") && (
            <div>
              <div className="hidden md:block">
                <table className="w-full table-auto divide-y divide-gray-200">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Location
                      </th>
                      {event.type === "trail_running" && (
                        <>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Distance
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            Elevation Gain
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                            COT
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {formatDate(selectedCategory.date)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {selectedCategory.time || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {event.location || "-"}
                      </td>
                      {event.type === "trail_running" && (
                        <>
                          <td className="px-4 py-4 text-sm text-gray-700">
                            {selectedCategory.distance != null
                              ? `${selectedCategory.distance} KM`
                              : "-"}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700">
                            {selectedCategory.elevationGain != null
                              ? `${selectedCategory.elevationGain} M`
                              : "-"}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700">
                            {selectedCategory.cot != null
                              ? `${selectedCategory.cot} Hours`
                              : "-"}
                          </td>
                        </>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="md:hidden">
                <div className="space-y-4 py-4 bg-white">
                  <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                      Date
                    </div>
                    <div className="text-sm text-gray-700">
                      {formatDate(selectedCategory.date)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                      Time
                    </div>
                    <div className="text-sm text-gray-700">
                      {selectedCategory.time || "-"}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                      Location
                    </div>
                    <div className="text-sm text-gray-700">
                      {event.location || "-"}
                    </div>
                  </div>
                  {event.type === "trail_running" && (
                    <>
                      <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                        <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                          Distance
                        </div>
                        <div className="text-sm text-gray-700">
                          {selectedCategory.distance != null
                            ? `${selectedCategory.distance} KM`
                            : "-"}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                        <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                          Elevation Gain
                        </div>
                        <div className="text-sm text-gray-700">
                          {selectedCategory.elevationGain != null
                            ? `${selectedCategory.elevationGain} M`
                            : "-"}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                        <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                          COT
                        </div>
                        <div className="text-sm text-gray-700">
                          {selectedCategory.cot != null
                            ? `${selectedCategory.cot} Hours`
                            : "-"}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="py-4 text-cyan-900">
            <p className="px-4 mb-4 text-lg leading-relaxed text-gray-700">
              {event.aboutEvent || "No event description available."}
            </p>

            {selectedCategory.gpxFileUrl && event.type === "trail_running" && (
              <div>
                <h4 className="px-4 text-md font-normal text-cyan-600 mb-4">
                  {viewMode === "map" && "MAP & GPX"}
                  {viewMode === "rundown" && "RUNDOWN"}
                </h4>
                {viewMode === "map" && selectedCategory.mapUrl && (
                  <div className="px-4 relative w-full h-[600px] rounded-xl overflow-hidden max-w-full">
                    <Iframe
                      url={selectedCategory.mapUrl}
                      width="100%"
                      height="600px"
                      className="rounded-xl"
                      display="block"
                      position="relative"
                    />
                  </div>
                )}
                {viewMode === "rundown" && (
                  <SectionRundown
                    rundown={selectedCategory.rundown}
                    formatDate={formatDate}
                    eventTitle={event.title}
                  />
                )}
              </div>
            )}

            {event.title === "Pelatihan Tenaga Olahraga Masyarakat" && (
              <div>
                <h4 className="px-4 text-md font-normal text-cyan-600 mb-4">
                  RUNDOWN
                </h4>
                <SectionRundown
                  rundown={null} // Tidak ada rundown dari Firestore, gunakan statis
                  formatDate={formatDate}
                  eventTitle={event.title}
                />
              </div>
            )}
          </div>

          {selectedCategory.gpxFileUrl && event.type === "trail_running" && (
            <div className="md:hidden bg-gray-100 text-base font-normal text-gray-400">
              <div className="flex flex-col divide-y divide-gray-300 border-y border-gray-300">
                <div className="relative">
                  <button
                    className={`w-full uppercase px-4 py-4 block transition-colors duration-300 whitespace-nowrap ${
                      viewMode === "map"
                        ? "bg-cyan-600 font-normal text-white"
                        : "hover:bg-gray-200"
                    }`}
                    onClick={() => setViewMode("map")}
                  >
                    MAP & GPX
                  </button>
                  {viewMode === "map" && (
                    <button
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white text-lg"
                      onClick={() =>
                        handleExportGpx(
                          selectedCategory.gpxFileUrl,
                          selectedCategory,
                          event
                        )
                      }
                      title="Unduh GPX"
                    >
                      <FontAwesomeIcon icon={faFileDownload} />
                    </button>
                  )}
                </div>
                <button
                  className={`w-full uppercase px-4 py-4 block transition-colors duration-300 whitespace-nowrap ${
                    viewMode === "rundown"
                      ? "bg-cyan-600 font-normal text-white"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={() => setViewMode("rundown")}
                >
                  RUNDOWN
                </button>
              </div>
            </div>
          )}

          {selectedCategory.gpxFileUrl && event.type === "trail_running" && (
            <div className="hidden md:flex items-center justify-between bg-gray-200 text-base font-normal text-gray-400 border-b border-gray-300">
              <div>
                {viewMode === "map" && (
                  <button
                    className="text-cyan-600 px-4 flex items-center space-x-2"
                    onClick={() =>
                      handleExportGpx(
                        selectedCategory.gpxFileUrl,
                        selectedCategory,
                        event
                      )
                    }
                  >
                    <span>Unduh GPX</span>
                    <FontAwesomeIcon icon={faFileDownload} />
                  </button>
                )}
              </div>
              <div className="flex space-x-0 divide-x divide-gray-300">
                <button
                  className={`flex-1 text-center uppercase px-4 py-4 transition-colors duration-300 whitespace-nowrap ${
                    viewMode === "map"
                      ? "bg-gray-100 font-normal text-cyan-600"
                      : "hover:bg-gray-300"
                  }`}
                  onClick={() => setViewMode("map")}
                >
                  MAP & GPX
                </button>
                <button
                  className={`flex-1 text-center uppercase px-4 py-4 transition-colors duration-300 whitespace-nowrap ${
                    viewMode === "rundown"
                      ? "bg-gray-100 font-normal text-cyan-600"
                      : "hover:bg-gray-300"
                  }`}
                  onClick={() => setViewMode("rundown")}
                >
                  RUNDOWN
                </button>
              </div>
            </div>
          )}

          <div className="mb-4">
            <div className="hidden md:block">
              <table className="w-full table-auto divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Harga
                    </th>
                    {event.eventId && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        Peserta
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Navigasi
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm text-gray-700 flex items-center">
                      <div
                        className={`w-2.5 h-2.5 rounded-full mr-2 ${
                          event.status === "Registrasi Dibuka"
                            ? "bg-green-500"
                            : event.status === "Registrasi Ditutup"
                            ? "bg-red-500"
                            : "bg-yellow-500"
                        }`}
                      ></div>
                      {event.status}
                    </td>
                    <td className="px-4 py-4 text-sm font-semibold text-cyan-700">
                      {selectedCategory.price != null
                        ? selectedCategory.price > 0
                          ? `Rp ${selectedCategory.price.toLocaleString(
                              "id-ID"
                            )}`
                          : "Gratis"
                        : event.price > 0
                        ? `Rp ${event.price.toLocaleString("id-ID")}`
                        : "Gratis"}
                    </td>
                    {event.eventId && (
                      <td className="px-4 py-4 text-sm text-gray-700">
                        <Link
                          to={`/entrylist/${event.eventId}/${
                            selectedCategory.distance || "all"
                          }`}
                          className="text-cyan-600 underline"
                        >
                          Lihat Peserta
                        </Link>
                      </td>
                    )}
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {event.websiteUrl ? (
                        <Link
                          to={event.websiteUrl}
                          className="text-cyan-600 underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Kunjungi Website Kegiatan
                        </Link>
                      ) : (
                        <span className="text-gray-400">
                          Website tidak tersedia
                        </span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="md:hidden">
              <div className="space-y-4 py-4 bg-white">
                <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                  <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                    Status
                  </div>
                  <div className="text-sm text-gray-700 flex items-center">
                    <div
                      className={`w-2.5 h-2.5 rounded-full mr-2 ${
                        event.status === "Registrasi Dibuka"
                          ? "bg-green-500"
                          : event.status === "Registrasi Ditutup"
                          ? "bg-red-500"
                          : "bg-yellow-500"
                      }`}
                    ></div>
                    {event.status}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                  <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                    Harga
                  </div>
                  <div className="text-sm font-semibold text-cyan-600">
                    {selectedCategory.price != null
                      ? selectedCategory.price > 0
                        ? `Rp ${selectedCategory.price.toLocaleString("id-ID")}`
                        : "Gratis"
                      : event.price > 0
                      ? `Rp ${event.price.toLocaleString("id-ID")}`
                      : "Gratis"}
                  </div>
                </div>
                {event.eventId && (
                  <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                    <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                      Peserta
                    </div>
                    <div className="text-sm text-gray-700">
                      <Link
                        to={`/entrylist/${event.eventId}/${
                          selectedCategory.distance || "all"
                        }`}
                        className="text-cyan-600 underline"
                      >
                        Lihat Peserta
                      </Link>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2 border-b border-gray-200 pb-3 px-4">
                  <div className="text-sm font-medium text-gray-600 uppercase tracking-wider">
                    Navigasi
                  </div>
                  <div className="text-sm text-gray-700">
                    {event.websiteUrl ? (
                      <Link
                        to={event.websiteUrl}
                        className="text-cyan-600 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Kunjungi Website Kegiatan
                      </Link>
                    ) : (
                      <span className="text-gray-400">
                        Website tidak tersedia
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-cyan-900">
            <p className="px-4 mb-4 text-lg leading-relaxed text-gray-700">
              {selectedCategory.description || "Tidak ada deskripsi tersedia."}
            </p>
          </div>

          {event.type === "trail_running" && <SectionQuicklinks />}

          <div className="mt-4">
            <div className="hidden md:block">
              <div className="flex items-center justify-end bg-gray-100 text-base font-normal text-gray-400">
                <div className="flex justify-end space-x-0 divide-x divide-gray-300">
                  {registrationStatus === "registered" &&
                  transactionStatus === "settlement" ? (
                    <span className="flex-1 text-center uppercase px-4 py-4 bg-cyan-600 text-white whitespace-nowrap">
                      ANDA SUDAH TERDAFTAR
                    </span>
                  ) : registrationStatus === "registered" &&
                    transactionStatus === "pending" ? (
                    <button
                      onClick={handleBayar}
                      className="flex-1 text-center uppercase px-4 py-4 transition-colors duration-300 bg-green-600 font-normal text-white whitespace-nowrap hover:bg-green-800"
                      disabled={!isSnapReady || !snapToken}
                    >
                      SELESAIKAN PEMBAYARAN
                    </button>
                  ) : (
                    /*
                      <button
                        onClick={() => handleRegister(selectedCategory)}
                        className={`flex-1 text-center uppercase px-4 py-4 transition-colors duration-300 whitespace-nowrap ${
                          isRegistrationOpen
                            ? "bg-green-600 font-normal text-white hover:bg-green-800"
                            : "text-white bg-slate-400 opacity-50 cursor-not-allowed"
                        }`}
                        disabled={!isRegistrationOpen}
                      >
                        DAFTAR KEGIATAN
                      </button>
                      */ null
                      
                  )}
                </div>
              </div>
            </div>
            <div className="md:hidden">
              <div className="bg-gray-100 text-base font-normal text-gray-400">
                <div className="flex flex-col divide-y divide-gray-300">
                  <div>
                    {registrationStatus === "registered" &&
                    transactionStatus === "settlement" ? (
                      <span className="w-full text-center uppercase px-4 py-4 bg-cyan-600 text-white block transition-colors duration-300 whitespace-nowrap">
                        ANDA SUDAH TERDAFTAR
                      </span>
                    ) : registrationStatus === "registered" &&
                      transactionStatus === "pending" ? (
                      <button
                        onClick={handleBayar}
                        className="w-full text-center uppercase px-4 py-4 block transition-colors duration-300 bg-green-600 font-normal text-white whitespace-nowrap hover:bg-green-800"
                        disabled={!isSnapReady || !snapToken}
                      >
                        SELESAIKAN PEMBAYARAN
                      </button>
                    ) : (
                      /*
                      <button
                        onClick={() => handleRegister(selectedCategory)}
                        className={`w-full text-center uppercase px-4 py-4 block transition-colors duration-300 whitespace-nowrap ${
                          isRegistrationOpen
                            ? "bg-green-600 font-normal text-white hover:bg-green-800"
                            : "text-white bg-slate-400 opacity-50 cursor-not-allowed"
                        }`}
                        disabled={!isRegistrationOpen}
                      >
                        DAFTAR KEGIATAN
                      </button>
                      */ null
                      
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
});
